// bugattiSlice.ts
import { createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { createAsyncThunk } from "@reduxjs/toolkit";
import { addToQueue } from '@rhino/shared/services/redux/prefsSlice';
import { fnFormatJSON, fnDictSetIndex, fnDictGetIndex } from '@rhino/shared/services/shared';
import { fnPullMediaImageSource, fnPullSessionMediaMeta } from '@rhino/shared/services/sputnikapi';
import { AppDispatch } from './store';

interface ExitState {
  complete: boolean;
  desc: string;
  code: string;
}

interface DeviceLastRun {
  state: string;
  exitstate: ExitState;
  manualrunpending?: number;
}

interface ServiceLastRun {
  lastrun: number;
  state: string;
}

export interface BugattiImage {
  mediaID: number;
  fileID?: number | null;
  fileName?: string;
  fileSize?: number | null;
  mimeType?: string;
  attributes?: string;
  imageSrc?: string;
  createddate?:string
  duration?:number|string
}
export interface DeviceStatus {
  service: ServiceLastRun;
  device: DeviceLastRun;
  media: {complete?:number,latest?:number,images:BugattiImage[]}
}



interface SharedSettings {
  sessionlength?: number;
  probe?: boolean;
  capturesettings?: CaptureSettings;
}


interface CaptureSettings {
  transform: string;
  webrtc: WebRTCSettings;
  imagesave: ImageSaveSettings;
  videosave: VideoSaveSettings;
  audiosave: AudioSaveSettings;
}

interface WebRTCSettings {
  videocodec: string;
  audiocodec: string;
  extendstream: number;
}

interface ImageSaveSettings {
  enabled: boolean;
  repeats: number;
  interval: number;
  jpgquality: number;
  format: string;
  filenameprefix: string;
}

interface VideoSaveSettings {
  enabled: boolean;
  writepath: string;
  maxvideolength: number;
  codecname: string;
  segment: {
    interval: number;
  };
  format: string;
  buffersize: number;
  filenameprefix: string;
}

interface AudioSaveSettings {
// Currently, it's empty but can be extended in the future
}
export interface BugattiSettings {
  continuouscapture?: SharedSettings
  devices: { [deviceName: string]: DeviceSettings };
}

interface DeviceCaptureSettings extends CaptureSettings {
  customname: string;
  devicename: string;
  device_id: string;
}

interface DeviceSettings {
  type: string;  // nestcam
  projectname: string;
  saveoffset: number;
  maxfiles: number;
  maxtime: number;
  active: boolean;
  manualrunpending: boolean; 
  capturesettings: DeviceCaptureSettings;
}

interface BugattiState {
  settings : BugattiSettings
  devicesStatus: { [deviceName: string]: DeviceStatus };
}

const initialState: BugattiState = {
  settings: {devices:{}},
  devicesStatus: {},
};

export const updateDeviceSettingAsync = createAsyncThunk(
  "bugattiSlice/updateDeviceSetting",
  async (payload: { deviceName: string, keyaddress: string; keyvalue: any; }, thunkAPI) => {
    const { dispatch,getState } = thunkAPI;
    const newKeyAddress = `devices.${payload.deviceName}.${payload.keyaddress}`;
    // console.log('send update to server',`keyaddress: ${newKeyAddress}`,`keyvalue: ${payload.keyvalue}`)
    dispatch(addToQueue({
      type: 3,
      prefname: 'acquire',
      keyaddress: newKeyAddress,
      keyvalue: payload.keyvalue
    }));
    dispatch(updateDeviceSetting(payload));

    return payload; // Return the payload
  }
);

export const updateDeviceStatusAsync = createAsyncThunk(
  "bugattiSlice/updateObjectsStatus",
  async (payload: { deviceName: string, keyaddress: string; keyvalue: any; }, thunkAPI) => {
    const { dispatch } = thunkAPI;

    dispatch(updateObjectsStatus(payload));

    return payload; // Return the payload
  }
);
export const updateDeviceImageAsync = createAsyncThunk<
  void, // Return type of the payload creator
  { deviceName: string; image: BugattiImage }, // First parameter's type
  { dispatch: AppDispatch } // ThunkAPI type
>(
  'bugattiSlice/updateDeviceImage',
  async (
    payload: { deviceName: string; image: BugattiImage },
    thunkAPI
  ) => {
    const { image } = payload;
    const { dispatch } = thunkAPI;
    let newImageInfo = { ...image };

    if (newImageInfo.mediaID && !newImageInfo.fileID) {
      try {
        const action = unwrapResult(await dispatch(pullSessionMediaMeta({
          bugattiImage: newImageInfo
        })));
        newImageInfo = action; 
      } catch (oError) {console.error(oError)}
    }

    // After executing pullSessionMediaMeta, execute this
    if (!newImageInfo?.imageSrc && newImageInfo.fileID) {
      try {
        const action = unwrapResult(await dispatch(pullMediaImageSource({
          bugattiImage: newImageInfo
        })));
        newImageInfo = action; 
      } catch (oError) {console.error(oError)}
    } 
    // console.log(newImageInfo)
    dispatch(updateDeviceImages({deviceName:payload.deviceName,image:newImageInfo}));
  }
);

interface PullSessionMediaMetaPayload {
  bugattiImage: BugattiImage;
}
export const pullSessionMediaMeta = createAsyncThunk<
  BugattiImage, // Return type
  PullSessionMediaMetaPayload, // First parameter's type
  { dispatch: AppDispatch }  // ThunkAPI type
>(
  'bugattiSlice/pullSessionMediaMeta',
  async ({ bugattiImage }: PullSessionMediaMetaPayload, { dispatch }) => {
    const result = await fnPullSessionMediaMeta(bugattiImage.mediaID, dispatch);
    if (result.success && Array.isArray(result.data.content.data[0])) {
      const [fileIDData, fileNameData, fileSizeData, mimeTypeData, attributesData] = result.data.content.data[0];
      const newImage: BugattiImage = {
        ...bugattiImage,
        fileID: fileIDData,
        fileName: fileNameData,
        fileSize: fileSizeData,
        mimeType: mimeTypeData,
        attributes: attributesData
      }
      return newImage;
    } else {
      throw new Error('Unable to pull image event');
    }
  }
);


interface PullMediaImageSourcePayload {
  bugattiImage: BugattiImage;
}

export const pullMediaImageSource = createAsyncThunk<
  BugattiImage, // Return type
  PullMediaImageSourcePayload, // First parameter's type
  { dispatch: AppDispatch }  // ThunkAPI type
>(
  'bugattiSlice/pullMediaImageSource',
  async ({ bugattiImage }: PullMediaImageSourcePayload, { dispatch }) => {
    if (!bugattiImage.fileID) {
      throw new Error('Unable to pull image source without a valid fileID');
    }
    const result = await fnPullMediaImageSource(bugattiImage.fileID, dispatch);

    if (result.success) {
      const newImage: BugattiImage = {
        ...bugattiImage,
        imageSrc: result.data.content.data.url
      };
      return newImage;
    } else {
      throw new Error('Unable to pull image source url');
    }
  }
);

const bugattiSlice = createSlice({
  name: 'bugatti',
  initialState,
  reducers: {
    setDeviceStatus: (state, action: PayloadAction<{ deviceName: string, data: DeviceStatus }>) => {
      // replace the entire state
      // console.log('setting',`state.devicesStatus[${action.payload.deviceName}]`,action.payload.data)
      state.devicesStatus[action.payload.deviceName] = action.payload.data;
      // console.log(JSON.stringify(state.devicesStatus))
    },
    updateObjectsStatus: (state, action: PayloadAction<{ deviceName: string, keyaddress: string, keyvalue: any }>) => {
      const { deviceName, keyaddress, keyvalue } = action.payload;
      // console.log(deviceName, keyaddress, keyvalue)
      if (state.devicesStatus[deviceName]) {
        fnDictSetIndex(state.devicesStatus[deviceName], keyaddress, keyvalue);
      }
    },
    setSettings: (state, action: PayloadAction<any>) => {
      // console.log('setting',`state.settings`,action.payload)
      state.settings = action.payload;
    },
    updateDeviceSetting: (state, action: PayloadAction<{ deviceName: string, keyaddress: string, keyvalue: any }>) => {
      const { deviceName, keyaddress, keyvalue } = action.payload;
      
      if (state.settings.devices[deviceName]) {
        fnDictSetIndex(state.settings.devices[deviceName], keyaddress, keyvalue);
      }
    },    
    updateDeviceImages: (state, action: PayloadAction<{ deviceName: string, image: BugattiImage }>) => {
      // console.log(action.payload)
      const { deviceName, image } = action.payload;
      let deviceStatus = state.devicesStatus[deviceName];
    
      // if (!deviceStatus.media) {
      //   deviceStatus.media = { images: [] };
      //   state.devicesStatus[deviceName].media=deviceStatus.media
      // }
    
      const images:BugattiImage[] = fnDictGetIndex(deviceStatus,'media.images',undefined,[]);
      const foundIndex = images.findIndex(img => img.mediaID === image.mediaID);
      const tAttributes:any = fnFormatJSON(image.attributes || {}, {
            "format": {
              "duration": {
                "formatter": "duration",
                "options": {
                  "precision": "0",
                  "sourceUnits": 'ns'
                }
              }
            },
            "properties": {
              "date:create": {
                "formatter": "intldate",
                "options": {
                  "dateFormat": "en-GB",
                  "dateFormatOptions": { dateStyle: 'short', timeStyle: 'short' }
                }
              }
            }
          },{})
      const newImage = { ...image };
      // console.log(tAttributes?.properties)
      newImage.createddate = tAttributes?.properties?.['date:create'];
      newImage.duration = tAttributes?.properties?.duration;
      const newComplete = !!newImage.imageSrc 
      // console.log(deviceName, newImage,newComplete)
      
      // console.log(newImage,newComplete)
      if (foundIndex >= 0) {
        // Update the existing image in the images array
        if (!deviceStatus.media.latest) fnDictSetIndex(state.devicesStatus,`${deviceName}.media.latest`, foundIndex)
        images[foundIndex] = newImage;
        if (newComplete) {
          fnDictSetIndex(state.devicesStatus,`${deviceName}.media.complete`, foundIndex)
        }
      } else {
        // Otherwise, insert the image at the end of the images array
        const newIndex = images.push(newImage) - 1;
        fnDictSetIndex(state.devicesStatus,`${deviceName}.media.latest`, newIndex)
        if (newComplete) {
          fnDictSetIndex(state.devicesStatus,`${deviceName}.media.complete`, newIndex)
        }
      }
      fnDictSetIndex(state.devicesStatus,`${deviceName}.media.images`, images)
    },
    
    
  },
});

export const { setDeviceStatus, updateObjectsStatus, setSettings, updateDeviceSetting, updateDeviceImages } = bugattiSlice.actions;
export const selectDeviceStatusByName = (state: { bugatti: BugattiState }, deviceName: string) => state.bugatti.devicesStatus[deviceName];
export const selectDevicesStatuses = (state: { bugatti: BugattiState }) => state.bugatti.devicesStatus;
export const selectBugattiServiceSettings = (state: { bugatti: BugattiState }) => state.bugatti.settings;
export const selectBugattiDeviceSettingsByName = (state: { bugatti: BugattiState }, deviceName: string) => state.bugatti.settings.devices[deviceName];
export const selectLatestDeviceImage = (state: { bugatti: BugattiState }, deviceName: string) => {
  // console.log(state.bugatti.devicesStatus)
  const deviceStatus = state.bugatti.devicesStatus[deviceName];
  if (deviceStatus && deviceStatus.media?.images && typeof deviceStatus.media.latest === 'number') {
    return deviceStatus.media.images[deviceStatus.media.latest];
  }
  return null;
};
export const selectCompleteDeviceImage = (state: { bugatti: BugattiState }, deviceName: string) => {
  const deviceStatus = state.bugatti.devicesStatus[deviceName];
  if (deviceStatus && deviceStatus.media?.images && typeof deviceStatus.media.complete === 'number') {
    return deviceStatus.media.images[deviceStatus.media.complete];
  }
  return null;
};


export default bugattiSlice.reducer;
